import React, { useState, useEffect } from 'react';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { Avatar } from 'primereact/avatar';
import { Tag } from 'primereact/tag';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';

interface BranchDetailsData {
  branch_id: string;
  institute_id: string;
  name: string;
  license_number: string;
  license_start: string;
  license_expiry: string;
  address: string;
  pincode: string;
  total_pc: number;
  latitude: number;
  longitude: number;
  opening_time: string;
  closing_time: string;
  capacity: number;
  status: string;
  active_operator: {
    user_id: string;
    name: string;
    email: string;
    role: string;
    assigned_at: string;
  } | null;
  created_at: string;
}

interface ApiResponse {
  success: boolean;
  data: BranchDetailsData;
}

interface BranchDetailsProps {
  branchId: string;
  onBack: () => void;
  onBranchLoad?: (branchName: string) => void;
}

const BranchDetails: React.FC<BranchDetailsProps> = ({ branchId, onBack, onBranchLoad }) => {
  const [branch, setBranch] = useState<BranchDetailsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchBranchDetails();
  }, [branchId]);

  const fetchBranchDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiClient.get<ApiResponse>(`/v1/branches/${branchId}`);
      
      if (response.data.success) {
        setBranch(response.data.data);
        if (onBranchLoad) {
          onBranchLoad(response.data.data.name);
        }
      } else {
        setError('Failed to load branch details');
      }
    } catch (err: any) {
      console.error('Failed to fetch branch details:', err);
      setError(err.response?.data?.error || 'Failed to load branch details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const formatTime = (timeString: string) => {
    try {
      const [hours, minutes] = timeString.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${minutes} ${ampm}`;
    } catch {
      return timeString;
    }
  };

  const calculateLicenseDaysRemaining = (expiryDate: string) => {
    const expiry = new Date(expiryDate).getTime();
    const now = Date.now();
    const daysRemaining = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
    return daysRemaining;
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px',
        background: '#f5f5f5',
        borderRadius: '12px'
      }}>
        <div style={{ textAlign: 'center' }}>
          <ProgressSpinner 
            style={{ width: '50px', height: '50px' }} 
            strokeWidth="3"
          />
          <p style={{
            marginTop: '16px',
            color: '#0f3d3e',
            fontSize: '0.95rem'
          }}>
            Loading branch details...
          </p>
        </div>
      </div>
    );
  }

  if (error || !branch) {
    return (
      <div>
        <Button
          icon="pi pi-arrow-left"
          label="Back to Branches"
          onClick={onBack}
          outlined
          style={{ marginBottom: '20px' }}
        />
        <Message 
          severity="error" 
          text={error || 'Branch not found'}
        />
      </div>
    );
  }

  const daysRemaining = calculateLicenseDaysRemaining(branch.license_expiry);
  const isLicenseExpiring = daysRemaining < 30 && daysRemaining > 0;
  const isLicenseExpired = daysRemaining <= 0;

  return (
    <div style={{ padding: '0' }}>
      {/* Header Section with Hero Banner */}
      <div style={{
        background: 'linear-gradient(135deg, #0f3d3e 0%, #1a5555 50%, #0a2f2f 100%)',
        borderRadius: '20px',
        padding: '32px',
        marginBottom: '24px',
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 10px 40px rgba(15, 61, 62, 0.3)'
      }}>
        {/* Decorative elements */}
        <div style={{
          position: 'absolute',
          top: '-50px',
          right: '-50px',
          width: '200px',
          height: '200px',
          background: 'rgba(212, 241, 126, 0.1)',
          borderRadius: '50%',
          filter: 'blur(40px)'
        }} />
        <div style={{
          position: 'absolute',
          bottom: '-80px',
          left: '-80px',
          width: '250px',
          height: '250px',
          background: 'rgba(212, 241, 126, 0.08)',
          borderRadius: '50%',
          filter: 'blur(50px)'
        }} />

        <Button
          icon="pi pi-arrow-left"
          label="Back to Branches"
          onClick={onBack}
          text
          style={{
            color: 'white',
            marginBottom: '10px',
            marginLeft: '-20px',
            fontSize: '0.9rem',
            position: 'relative',
            zIndex: 1
          }}
        />
        
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: '24px',
          position: 'relative',
          zIndex: 1
        }}>
          <div style={{ flex: 1, minWidth: '250px' }}>
            <div style={{
              display: 'inline-block',
              padding: '5px 12px',
              background: 'rgba(212, 241, 126, 0.2)',
              borderRadius: '20px',
              marginBottom: '10px',
              fontSize: '0.7rem',
              fontWeight: '600',
              letterSpacing: '0.5px',
              textTransform: 'uppercase'
            }}>
              Branch Details
            </div>
            <h1 style={{
              margin: '0 0 8px 0',
              fontSize: '1.7rem',
              fontWeight: '700',
              color: 'white',
              letterSpacing: '-0.5px'
            }}>
              {branch.name}
            </h1>
            <p style={{
              margin: '0 0 20px 0',
              color: 'rgba(255,255,255,0.8)',
              fontSize: '0.9rem'
            }}>
              {branch.address} • PIN: {branch.pincode}
            </p>

            {/* Quick Stats */}
            <div style={{
              display: 'flex',
              gap: '16px',
              flexWrap: 'wrap'
            }}>
              <div style={{
                background: 'rgba(255,255,255,0.1)',
                backdropFilter: 'blur(10px)',
                padding: '12px 16px',
                borderRadius: '12px',
                border: '1px solid rgba(255,255,255,0.2)'
              }}>
                <p style={{
                  margin: '0 0 4px 0',
                  fontSize: '0.7rem',
                  color: 'rgba(255,255,255,0.7)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Capacity
                </p>
                <p style={{
                  margin: 0,
                  fontSize: '1rem',
                  fontWeight: '700',
                  color: '#d4f17e'
                }}>
                  {branch.capacity}
                </p>
              </div>

              <div style={{
                background: 'rgba(255,255,255,0.1)',
                backdropFilter: 'blur(10px)',
                padding: '12px 16px',
                borderRadius: '12px',
                border: '1px solid rgba(255,255,255,0.2)'
              }}>
                <p style={{
                  margin: '0 0 4px 0',
                  fontSize: '0.7rem',
                  color: 'rgba(255,255,255,0.7)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Total PCs
                </p>
                <p style={{
                  margin: 0,
                  fontSize: '1rem',
                  fontWeight: '700',
                  color: '#d4f17e'
                }}>
                  {branch.total_pc}
                </p>
              </div>

              <div style={{
                background: 'rgba(255,255,255,0.1)',
                backdropFilter: 'blur(10px)',
                padding: '12px 16px',
                borderRadius: '12px',
                border: '1px solid rgba(255,255,255,0.2)'
              }}>
                <p style={{
                  margin: '0 0 4px 0',
                  fontSize: '0.7rem',
                  color: 'rgba(255,255,255,0.7)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Hours
                </p>
                <p style={{
                  margin: 0,
                  fontSize: '1rem',
                  fontWeight: '600',
                  color: '#d4f17e'
                }}>
                  {formatTime(branch.opening_time)} - {formatTime(branch.closing_time)}
                </p>
              </div>
            </div>
          </div>

          <div style={{ textAlign: 'right' }}>
            <Tag
              value={branch.status.toUpperCase()}
              severity={branch.status === 'active' ? 'success' : 'danger'}
              style={{
                fontSize: '0.7rem',
                fontWeight: '600',
                padding: '5px 12px',
                background: branch.status === 'active' ? '#d4f17e' : '#ff6b6b',
                color: branch.status === 'active' ? '#0f3d3e' : 'white',
                border: 'none'
              }}
            />
            <p style={{
              margin: '8px 0 0 0',
              fontSize: '0.75rem',
              color: 'rgba(255,255,255,0.6)',
              fontFamily: 'monospace'
            }}>
              ID: {branch.branch_id}
            </p>
          </div>
        </div>
      </div>

      {/* License Warning */}
      {(isLicenseExpiring || isLicenseExpired) && (
        <Message
          severity={isLicenseExpired ? 'error' : 'warn'}
          text={
            isLicenseExpired
              ? `⚠️ License expired ${Math.abs(daysRemaining)} days ago - Immediate action required`
              : `⏰ License expiring in ${daysRemaining} days - Please renew soon`
          }
          style={{ 
            marginBottom: '20px',
            fontSize: '0.85rem',
            fontWeight: '500'
          }}
        />
      )}

      {/* Main Content - Two Column Layout */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '2fr 1fr',
        gap: '20px',
        marginBottom: '24px'
      }}>
        {/* Left Column - Main Information */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* License Information Section */}
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '24px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            border: '1px solid #f0f0f0'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '20px'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                background: 'linear-gradient(135deg, #0f3d3e 0%, #1a5555 100%)',
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <i className="pi pi-id-card" style={{ color: '#d4f17e', fontSize: '1.1rem' }}></i>
              </div>
              <div>
                <h2 style={{
                  margin: 0,
                  fontSize: '1.1rem',
                  fontWeight: '700',
                  color: '#0f3d3e'
                }}>
                  License Information
                </h2>
                <p style={{
                  margin: '2px 0 0 0',
                  fontSize: '0.75rem',
                  color: '#999'
                }}>
                  Official license details and validity
                </p>
              </div>
            </div>

            <div style={{
              background: '#f8f9fa',
              padding: '16px',
              borderRadius: '10px',
              marginBottom: '16px'
            }}>
              <p style={{
                margin: '0 0 6px 0',
                fontSize: '0.7rem',
                color: '#666',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                License Number
              </p>
              <p style={{
                margin: 0,
                fontSize: '1.2rem',
                fontWeight: '700',
                color: '#0f3d3e',
                fontFamily: 'monospace',
                letterSpacing: '1px'
              }}>
                {branch.license_number}
              </p>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              gap: '12px'
            }}>
              <div>
                <p style={{
                  margin: '0 0 6px 0',
                  fontSize: '0.7rem',
                  color: '#999',
                  fontWeight: '600',
                  textTransform: 'uppercase'
                }}>
                  Start Date
                </p>
                <p style={{
                  margin: 0,
                  fontSize: '0.85rem',
                  fontWeight: '600',
                  color: '#333'
                }}>
                  {formatDate(branch.license_start)}
                </p>
              </div>

              <div>
                <p style={{
                  margin: '0 0 6px 0',
                  fontSize: '0.7rem',
                  color: '#999',
                  fontWeight: '600',
                  textTransform: 'uppercase'
                }}>
                  Expiry Date
                </p>
                <p style={{
                  margin: 0,
                  fontSize: '0.85rem',
                  fontWeight: '700',
                  color: isLicenseExpired ? '#dc3545' : isLicenseExpiring ? '#ff9800' : '#4caf50'
                }}>
                  {formatDate(branch.license_expiry)}
                </p>
              </div>

              <div>
                <p style={{
                  margin: '0 0 6px 0',
                  fontSize: '0.7rem',
                  color: '#999',
                  fontWeight: '600',
                  textTransform: 'uppercase'
                }}>
                  Days Remaining
                </p>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <i
                    className={`pi ${isLicenseExpired ? 'pi-times-circle' : isLicenseExpiring ? 'pi-exclamation-triangle' : 'pi-check-circle'}`}
                    style={{
                      fontSize: '1rem',
                      color: isLicenseExpired ? '#dc3545' : isLicenseExpiring ? '#ff9800' : '#4caf50'
                    }}
                  ></i>
                  <p style={{
                    margin: 0,
                    fontSize: '0.95rem',
                    fontWeight: '700',
                    color: isLicenseExpired ? '#dc3545' : isLicenseExpiring ? '#ff9800' : '#4caf50'
                  }}>
                    {daysRemaining > 0 ? daysRemaining : 'Expired'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Location Section */}
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '24px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            border: '1px solid #f0f0f0'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '20px'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                background: 'linear-gradient(135deg, #0f3d3e 0%, #1a5555 100%)',
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <i className="pi pi-map-marker" style={{ color: '#d4f17e', fontSize: '1.1rem' }}></i>
              </div>
              <div>
                <h2 style={{
                  margin: 0,
                  fontSize: '1.1rem',
                  fontWeight: '700',
                  color: '#0f3d3e'
                }}>
                  Location Details
                </h2>
                <p style={{
                  margin: '2px 0 0 0',
                  fontSize: '0.75rem',
                  color: '#999'
                }}>
                  Branch address and coordinates
                </p>
              </div>
            </div>

            <div style={{
              background: '#f8f9fa',
              padding: '16px',
              borderRadius: '10px',
              marginBottom: '16px'
            }}>
              <p style={{
                margin: '0 0 6px 0',
                fontSize: '0.7rem',
                color: '#666',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Full Address
              </p>
              <p style={{
                margin: 0,
                fontSize: '0.95rem',
                color: '#333',
                lineHeight: '1.5',
                fontWeight: '500'
              }}>
                {branch.address}
              </p>
              <p style={{
                margin: '6px 0 0 0',
                fontSize: '0.9rem',
                fontWeight: '700',
                color: '#0f3d3e'
              }}>
                Pincode: {branch.pincode}
              </p>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '12px',
              padding: '12px',
              background: 'linear-gradient(135deg, rgba(15, 61, 62, 0.05) 0%, rgba(26, 85, 85, 0.05) 100%)',
              borderRadius: '10px'
            }}>
              <div>
                <p style={{
                  margin: '0 0 4px 0',
                  fontSize: '0.7rem',
                  color: '#666',
                  fontWeight: '600',
                  textTransform: 'uppercase'
                }}>
                  Latitude
                </p>
                <p style={{
                  margin: 0,
                  fontSize: '0.9rem',
                  fontWeight: '700',
                  color: '#0f3d3e',
                  fontFamily: 'monospace'
                }}>
                  {branch.latitude}°
                </p>
              </div>
              <div>
                <p style={{
                  margin: '0 0 4px 0',
                  fontSize: '0.7rem',
                  color: '#666',
                  fontWeight: '600',
                  textTransform: 'uppercase'
                }}>
                  Longitude
                </p>
                <p style={{
                  margin: 0,
                  fontSize: '0.9rem',
                  fontWeight: '700',
                  color: '#0f3d3e',
                  fontFamily: 'monospace'
                }}>
                  {branch.longitude}°
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Sidebar Information */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* Active Operator */}
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '20px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            border: '1px solid #f0f0f0'
          }}>
            <h3 style={{
              margin: '0 0 16px 0',
              fontSize: '1rem',
              fontWeight: '700',
              color: '#0f3d3e'
            }}>
              Active Operator
            </h3>

            {branch.active_operator ? (
              <div>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  padding: '20px',
                  background: 'linear-gradient(135deg, #0f3d3e 0%, #1a5555 100%)',
                  borderRadius: '12px',
                  marginBottom: '16px'
                }}>
                  <Avatar
                    label={branch.active_operator.name.charAt(0)}
                    size="xlarge"
                    style={{
                      backgroundColor: '#d4f17e',
                      color: '#0f3d3e',
                      width: '64px',
                      height: '64px',
                      fontSize: '1.6rem',
                      fontWeight: '700',
                      marginBottom: '12px'
                    }}
                    shape="circle"
                  />
                  <p style={{
                    margin: '0 0 6px 0',
                    fontSize: '1.1rem',
                    fontWeight: '700',
                    color: 'white',
                    textAlign: 'center'
                  }}>
                    {branch.active_operator.name}
                  </p>
                  <p style={{
                    margin: '0 0 10px 0',
                    fontSize: '0.8rem',
                    color: 'rgba(255,255,255,0.8)',
                    textAlign: 'center',
                    wordBreak: 'break-all'
                  }}>
                    {branch.active_operator.email}
                  </p>
                  <Tag
                    value={branch.active_operator.role.toUpperCase()}
                    style={{
                      background: '#d4f17e',
                      color: '#0f3d3e',
                      fontSize: '0.7rem',
                      padding: '5px 10px',
                      fontWeight: '700'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <p style={{
                    margin: '0 0 6px 0',
                    fontSize: '0.7rem',
                    color: '#999',
                    fontWeight: '600',
                    textTransform: 'uppercase'
                  }}>
                    User ID
                  </p>
                  <p style={{
                    margin: 0,
                    fontSize: '0.8rem',
                    fontWeight: '600',
                    color: '#0f3d3e',
                    fontFamily: 'monospace',
                    background: '#f8f9fa',
                    padding: '8px 10px',
                    borderRadius: '8px'
                  }}>
                    {branch.active_operator.user_id}
                  </p>
                </div>

                <div>
                  <p style={{
                    margin: '0 0 6px 0',
                    fontSize: '0.7rem',
                    color: '#999',
                    fontWeight: '600',
                    textTransform: 'uppercase'
                  }}>
                    Assigned On
                  </p>
                  <p style={{
                    margin: 0,
                    fontSize: '0.8rem',
                    color: '#333',
                    fontWeight: '500'
                  }}>
                    {formatDate(branch.active_operator.assigned_at)}
                  </p>
                </div>
              </div>
            ) : (
              <div style={{
                padding: '32px 16px',
                textAlign: 'center',
                background: '#f8f9fa',
                borderRadius: '10px'
              }}>
                <i className="pi pi-user-minus" style={{
                  fontSize: '2.5rem',
                  color: '#ddd',
                  marginBottom: '12px',
                  display: 'block'
                }}></i>
                <p style={{
                  margin: '0 0 12px 0',
                  fontSize: '0.85rem',
                  color: '#999',
                  fontStyle: 'italic'
                }}>
                  No operator assigned
                </p>
                <Button
                  label="Assign Operator"
                  icon="pi pi-user-plus"
                  outlined
                  size="small"
                  style={{
                    color: '#0f3d3e',
                    borderColor: '#0f3d3e',
                    fontSize: '0.8rem'
                  }}
                />
              </div>
            )}
          </div>

          {/* Metadata */}
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '20px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            border: '1px solid #f0f0f0'
          }}>
            <h3 style={{
              margin: '0 0 16px 0',
              fontSize: '1rem',
              fontWeight: '700',
              color: '#0f3d3e'
            }}>
              Metadata
            </h3>

            <div style={{ marginBottom: '16px' }}>
              <p style={{
                margin: '0 0 6px 0',
                fontSize: '0.7rem',
                color: '#999',
                fontWeight: '600',
                textTransform: 'uppercase'
              }}>
                Institute ID
              </p>
              <p style={{
                margin: 0,
                fontSize: '0.8rem',
                fontWeight: '600',
                color: '#0f3d3e',
                fontFamily: 'monospace',
                background: '#f8f9fa',
                padding: '8px 10px',
                borderRadius: '8px',
                wordBreak: 'break-all'
              }}>
                {branch.institute_id}
              </p>
            </div>

            <div>
              <p style={{
                margin: '0 0 6px 0',
                fontSize: '0.7rem',
                color: '#999',
                fontWeight: '600',
                textTransform: 'uppercase'
              }}>
                Created At
              </p>
              <p style={{
                margin: 0,
                fontSize: '0.8rem',
                color: '#333',
                fontWeight: '500'
              }}>
                {formatDate(branch.created_at)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '20px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        border: '1px solid #f0f0f0',
        display: 'flex',
        gap: '12px',
        flexWrap: 'wrap',
        justifyContent: 'center'
      }}>
        <Button
          label="Edit Branch"
          icon="pi pi-pencil"
          style={{
            background: '#0f3d3e',
            border: 'none',
            padding: '12px 24px',
            fontSize: '0.85rem',
            fontWeight: '600'
          }}
        />
        <Button
          label="Assign Operator"
          icon="pi pi-user-plus"
          outlined
          style={{
            color: '#0f3d3e',
            borderColor: '#0f3d3e',
            padding: '12px 24px',
            fontSize: '0.85rem',
            fontWeight: '600'
          }}
        />
        <Button
          label="View Reports"
          icon="pi pi-chart-line"
          outlined
          style={{
            color: '#0f3d3e',
            borderColor: '#0f3d3e',
            padding: '12px 24px',
            fontSize: '0.85rem',
            fontWeight: '600'
          }}
        />
        <Button
          label="Delete Branch"
          icon="pi pi-trash"
          severity="danger"
          outlined
          style={{
            padding: '12px 24px',
            fontSize: '0.85rem',
            fontWeight: '600'
          }}
        />
      </div>
    </div>
  );
};

export default BranchDetails;